import { NgModule, LOCALE_ID, APP_INITIALIZER } from '@angular/core';
import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import localeRu from '@angular/common/locales/ru';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LayoutModule } from '@angular/cdk/layout';
import { MatSidenavModule } from '@angular/material/sidenav';
import { tuiProvideMobileCalendar } from '@taiga-ui/addon-mobile';
import { TuiTextfieldControllerModule, TuiInputModule, TuiSelectModule } from '@taiga-ui/legacy';
import { provideEventPlugins } from '@taiga-ui/event-plugins';
import { TUI_DATE_TIME_VALUE_TRANSFORMER, TuiInputFiles, TuiDataListWrapper, TuiAccordion } from '@taiga-ui/kit';
import { TUI_LANGUAGE } from '@taiga-ui/i18n';
import {
  TuiRoot,
  TuiAlert,
  TuiDataList,
  TuiScrollbar,
  TuiScrollable,
  TuiDialog,
  TuiButton,
  tuiScrollbarOptionsProvider,
} from '@taiga-ui/core';
import { NgxPermissionsModule } from 'ngx-permissions';
import { QRCodeModule } from 'angularx-qrcode';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { AngularYandexMapsModule } from 'angular8-yandex-maps';
import { TranslateModule, TranslateLoader, MissingTranslationHandler, TranslateService } from '@ngx-translate/core';
import {
  AuthModule,
  TokenInterceptor,
  AuthGuard,
  UnAuthGuard,
  SessionService,
  RedirectGuard,
  StorageService,
} from '@src/app/modules/auth';
import { PreferencesService, DynamicPermissionsService, PreviousRouteService } from '@src/core/services';
import { AppLayoutComponent } from '@src/app/app-layout';
import {
  BusinessTypesService,
  BusinessTypesForUnionService,
  ChatsService,
  CommitteesService,
  CommitteeSubscriptionsService,
  CommitteeUserSubscriptionsService,
  DashboardService,
  DocumentsService,
  EventService,
  AppService,
  JobTitlesService,
  JobTitlesForUnionService,
  InvitesService,
  LabelItemsService,
  OPFTypesService,
  OrganisationService,
  OrganisationSettingsService,
  PhotoService,
  PollsService,
  RoleService,
  SubscriptionsForOrganisationService,
  SubscriptionsForUsersService,
  TestService,
  TokenWithEmailService,
  UserService,
  MailsService,
  ObjectMenuService,
  LoyaltyService,
  BASE_PATH,
} from '@src/api';
import { BASE_PATH as BASE_PATH_MESSENGER } from '@src/api-messenger';
import { TuiDayTimeTransformer } from '@src/utils';
import { SharedModule } from '@src/app/shared';
import { GroupInfoModule } from '@src/app/modules/group-info';
import { LoginModule } from '@src/app/modules/login';
import { NAVIGATION_ITEMS, NAVIGATION_ITEMS_TOKEN, NavigationModule } from '@src/app/modules/navigation';
import { PageNotFoundModule } from '@src/app/pages/page-not-found';
import { IconModule } from '@src/app/modules/icon';
import { APP_CONFIG, Language, CoreModule } from '@src/core';
import { HttpLoaderFactory, appInitializerFactory, tuiLanguageFactory } from '@src/utils/localization';
import { RequestInterceptor } from '@src/core/interceptor/interceptor';
import { InternalMessengerModule } from '@src/app/modules/internal-messenger';
import { CustomNamesService } from '@src/app/modules/custom-name-tabs';
import { LpSettingsModule } from '@src/app/modules/lp-settings';
import { NotificationsService } from '@src/app/modules/settings/notifications';
import { TestingControlModule } from '@src/app/modules/testing-control';
import { ScrollPositionRestorationModule } from '@src/app/modules/scroll-position-restoration';
import { CordovaModule, CordovaService } from '@src/app/modules/cordova';
import { YA_MAP_CONFIG } from '@src/app/modules/map';

import { MissingTranslationService } from '../core/services/missing-translation-service';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ComponentsModule } from './components';
import { PolicyPageModule } from './pages/policy-page';

registerLocaleData(localeRu, Language.RU);

@NgModule({
  declarations: [AppComponent, AppLayoutComponent],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    FormsModule,
    GroupInfoModule,
    HttpClientModule,
    IconModule,
    LayoutModule,
    LoginModule,
    MatSidenavModule,
    NavigationModule,
    NgxPermissionsModule.forRoot(),
    PageNotFoundModule,
    SharedModule,
    ...TuiAccordion,
    TuiAlert,
    TuiButton,
    ...TuiDataList,
    ...TuiDataListWrapper,
    TuiDialog,
    TuiInputModule,
    TuiRoot,
    TuiScrollbar,
    TuiScrollable,
    TuiSelectModule,
    TuiTextfieldControllerModule,
    ComponentsModule,
    AuthModule,
    QRCodeModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: MissingTranslationService },
      useDefaultLang: false,
    }),
    InfiniteScrollModule,
    PolicyPageModule,
    LpSettingsModule,
    TestingControlModule,
    InternalMessengerModule,
    AngularYandexMapsModule.forRoot(YA_MAP_CONFIG),
    ScrollPositionRestorationModule.forRoot(),
    CordovaModule,
  ],
  providers: [
    SessionService,
    CustomNamesService,
    PreferencesService,
    NotificationsService,
    DynamicPermissionsService,
    PreviousRouteService,
    {
      provide: APP_INITIALIZER,
      useFactory:
        (
          session: SessionService,
          customNames: CustomNamesService,
          preferences: PreferencesService,
          notifications: NotificationsService,
          dynamicPermissions: DynamicPermissionsService,
          cordovaService: CordovaService,
          previousRouteService: PreviousRouteService,
        ) =>
        async () => {
          session.addCallbackAfterCreateSession(customNames.getData.bind(customNames));
          session.addCallbackAfterCreateSession(preferences.getData.bind(preferences));
          session.addCallbackAfterCreateSession(notifications.getData.bind(notifications));
          session.addCallbackAfterCreateSession(dynamicPermissions.setData.bind(dynamicPermissions));
          session.addCallbackAfterCreateSession(cordovaService.init.bind(cordovaService));
          session.addCallbackAfterCreateSession(previousRouteService.init.bind(previousRouteService));

          return session.init();
        },
      deps: [
        SessionService,
        CustomNamesService,
        PreferencesService,
        NotificationsService,
        DynamicPermissionsService,
        CordovaService,
        PreviousRouteService,
      ],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, StorageService],
      multi: true,
    },
    { provide: NAVIGATION_ITEMS_TOKEN, useValue: NAVIGATION_ITEMS },
    {
      provide: BASE_PATH,
      useValue: APP_CONFIG.baseUrl,
    },
    {
      provide: BASE_PATH_MESSENGER,
      useFactory: () => APP_CONFIG.messenger.baseUrl,
    },
    { provide: LOCALE_ID, useValue: Language.RU },
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    {
      provide: TUI_DATE_TIME_VALUE_TRANSFORMER,
      useClass: TuiDayTimeTransformer,
    },
    {
      provide: TUI_LANGUAGE,
      useFactory: tuiLanguageFactory,
      deps: [TranslateService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true,
    },
    tuiScrollbarOptionsProvider({
      mode: 'hover',
    }),
    AuthGuard,
    UnAuthGuard,
    BusinessTypesService,
    BusinessTypesForUnionService,
    ChatsService,
    CommitteesService,
    CommitteeSubscriptionsService,
    CommitteeUserSubscriptionsService,
    DashboardService,
    DocumentsService,
    EventService,
    AppService,
    RedirectGuard,
    JobTitlesService,
    JobTitlesForUnionService,
    InvitesService,
    LabelItemsService,
    OPFTypesService,
    OrganisationService,
    OrganisationSettingsService,
    PhotoService,
    PollsService,
    RoleService,
    SubscriptionsForOrganisationService,
    SubscriptionsForUsersService,
    TestService,
    TokenWithEmailService,
    UserService,
    MailsService,
    ObjectMenuService,
    LoyaltyService,
    provideEventPlugins(),
    tuiProvideMobileCalendar(),
    TuiInputFiles,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
